/**
 * RecordTable — a server-driven admin list: search, status/course/date filters, page size,
 * page buttons and an Export menu. The server does the filtering (server/utils/listQuery.js),
 * so the totals, badge counts and exports cover every matching record, not just this page.
 *
 * `fetchPage`, `statusOptions`, `courseOptions` and `dateFields` must be stable references
 * (adminApi functions / module-level constants) — a new array every render would refetch.
 *
 * Below md (768px) rows render as stacked label/value cards, like DataTable's default mobile
 * layout: built from the same columns and renderRow cells, so no page needs its own mobile markup.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";
import MDButton from "components/MDButton";
import ExportMenu from "components/ExportMenu";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import pageWindow from "./pageWindow";

const PAGE_SIZES = [10, 20, 50, 100];
const EMPTY_RESULT = { items: [], total: 0, totalPages: 1, statusCounts: {} };

// Filter controls share each toolbar row evenly on phones; from sm up they keep their fixed widths.
const filterSx = (minWidth) => ({
  flex: { xs: "1 1 140px", sm: "0 0 auto" },
  minWidth: { xs: 0, sm: minWidth },
});
const dateFilterSx = { flex: { xs: "1 1 140px", sm: "0 0 auto" }, width: { xs: "auto", sm: 160 } };

// Action-button columns move to a footer strip in the mobile card layout.
const isActionColumn = (column) => /^actions?$/i.test(column.accessor);

// Email columns may break inside the address (see DataTableBodyCell); a column can also opt in or
// out explicitly with `breakAnywhere: true/false`.
const isEmailColumn = (column) => /email/i.test(column.accessor);

// Columns whose Header is empty (e.g. an avatar or checkbox column) render full width, unlabelled.
const hasHeaderText = (column) =>
  typeof column.Header === "string" ? column.Header.trim() !== "" : Boolean(column.Header);

function RecordTable({
  columns,
  fetchPage,
  renderRow,
  statusOptions,
  statusLabel,
  courseOptions,
  dateFields,
  reloadToken,
  exportConfig,
  onLoaded,
  emptyMessage,
  defaultLimit,
}) {
  const theme = useTheme();
  const showCards = useMediaQuery(theme.breakpoints.down("md"));
  const initialFilters = {
    search: "",
    status: "",
    course: "",
    from: "",
    to: "",
    dateField: dateFields ? dateFields[0].value : "",
  };
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);
  const [result, setResult] = useState(EMPTY_RESULT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryToken, setRetryToken] = useState(0);

  // When the table still scrolls sideways, pin the actions column to the right edge so the row
  // buttons never need scrolling to (same behaviour as DataTable).
  const scrollRef = useRef(null);
  const [tableOverflows, setTableOverflows] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || showCards || typeof ResizeObserver === "undefined") {
      setTableOverflows(false);
      return undefined;
    }
    const check = () => setTableOverflows(el.scrollWidth > el.clientWidth + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    const tableEl = el.querySelector("table");
    if (tableEl) observer.observe(tableEl);
    return () => observer.disconnect();
  }, [showCards, result]);

  const latestRequest = useRef(0);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const hasDateFields = Boolean(dateFields && dateFields.length);

  const updateFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  // Debounced search — applied 300 ms after the last keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchInput.trim();
      if (next !== filtersRef.current.search) {
        setFilters((f) => ({ ...f, search: next }));
        setPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const params = useMemo(() => {
    const p = { page, limit };
    if (filters.search) p.search = filters.search;
    if (filters.status) p.status = filters.status;
    if (filters.course) p.course = filters.course;
    if (filters.from) p.from = filters.from;
    if (filters.to) p.to = filters.to;
    if (hasDateFields && filters.dateField) p.dateField = filters.dateField;
    return p;
  }, [page, limit, filters, hasDateFields]);

  useEffect(() => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoading(true);
    setError("");
    fetchPage(params)
      .then((res) => {
        if (latestRequest.current !== requestId) return;
        const totalPages = res.totalPages || 1;
        setResult({
          items: res.items || [],
          total: res.total || 0,
          totalPages,
          statusCounts: res.statusCounts || {},
        });
        if (onLoadedRef.current) onLoadedRef.current(res);
        if (params.page > totalPages) setPage(totalPages);
      })
      .catch((err) => {
        if (latestRequest.current !== requestId) return;
        setError(
          err.response?.data?.error || err.response?.data?.message || "Could not load records."
        );
      })
      .finally(() => {
        if (latestRequest.current === requestId) setLoading(false);
      });
  }, [params, reloadToken, retryToken, fetchPage]);

  const exportParams = useMemo(() => {
    const { page: _page, limit: _limit, ...rest } = params;
    return rest;
  }, [params]);
  const isFiltered = Object.keys(exportParams).some((key) => key !== "dateField");
  const statusTotal = Object.values(result.statusCounts).reduce((sum, n) => sum + n, 0);
  const firstRow = result.total === 0 ? 0 : (page - 1) * limit + 1;
  const lastRow = Math.min(page * limit, result.total);
  const fieldColumns = columns.filter((c) => !isActionColumn(c));
  const actionColumns = columns.filter(isActionColumn);

  const clearFilters = () => {
    setSearchInput("");
    setFilters(initialFilters);
    setPage(1);
  };

  return (
    <TableContainer sx={{ boxShadow: "none" }}>
      <MDBox
        display="flex"
        flexWrap="wrap"
        gap={1.5}
        alignItems="center"
        px={{ xs: 2, md: 3 }}
        pt={{ xs: 2, md: 3 }}
        pb={1}
      >
        <MDBox
          sx={{ flex: { xs: "1 1 100%", sm: "1 1 220px" }, maxWidth: { xs: "100%", sm: 320 } }}
        >
          <MDInput
            placeholder="Search name, email, phone…"
            value={searchInput}
            size="small"
            fullWidth
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </MDBox>
        {statusOptions && (
          <TextField
            select
            size="small"
            label={statusLabel}
            value={filters.status}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
            onChange={(e) => updateFilter("status", e.target.value)}
            sx={filterSx(170)}
          >
            <MenuItem value="">All ({statusTotal})</MenuItem>
            {statusOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label} ({result.statusCounts[o.value] || 0})
              </MenuItem>
            ))}
          </TextField>
        )}
        {courseOptions && (
          <TextField
            select
            size="small"
            label="Course"
            value={filters.course}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
            onChange={(e) => updateFilter("course", e.target.value)}
            sx={filterSx(170)}
          >
            <MenuItem value="">All courses</MenuItem>
            {courseOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        )}
        {hasDateFields && dateFields.length > 1 && (
          <TextField
            select
            size="small"
            label="Date"
            value={filters.dateField}
            onChange={(e) => updateFilter("dateField", e.target.value)}
            sx={filterSx(130)}
          >
            {dateFields.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          type="date"
          size="small"
          label="From"
          InputLabelProps={{ shrink: true }}
          value={filters.from}
          onChange={(e) => updateFilter("from", e.target.value)}
          sx={dateFilterSx}
        />
        <TextField
          type="date"
          size="small"
          label="To"
          InputLabelProps={{ shrink: true }}
          value={filters.to}
          onChange={(e) => updateFilter("to", e.target.value)}
          sx={dateFilterSx}
        />
        {(isFiltered || searchInput) && (
          <MDButton size="small" variant="text" color="secondary" onClick={clearFilters}>
            Clear
          </MDButton>
        )}
        {exportConfig && (
          <MDBox ml="auto">
            <ExportMenu
              {...exportConfig}
              params={exportParams}
              filtered={isFiltered}
              disabled={result.total === 0}
            />
          </MDBox>
        )}
      </MDBox>

      {error && (
        <MDBox
          px={{ xs: 2, md: 3 }}
          py={1.5}
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          gap={1.5}
        >
          <MDTypography variant="button" color="error">
            {error}
          </MDTypography>
          <MDButton
            size="small"
            variant="outlined"
            color="info"
            onClick={() => setRetryToken((t) => t + 1)}
          >
            Retry
          </MDButton>
        </MDBox>
      )}

      <MDBox
        ref={scrollRef}
        sx={{ opacity: loading ? 0.55 : 1, transition: "opacity 0.15s ease", overflowX: "auto" }}
      >
        {showCards ? (
          <MDBox px={{ xs: 1.5, sm: 2 }} pb={1} display="flex" flexDirection="column" gap={1.5}>
            {result.items.map((item, index) => {
              const cells = renderRow(item);
              return (
                <MDBox
                  key={item._id || index}
                  role="row"
                  sx={({ palette: { light }, borders: { borderWidth, borderRadius } }) => ({
                    border: `${borderWidth[1]} solid ${light.main}`,
                    borderRadius: borderRadius.lg,
                    px: 1.5,
                    py: 1,
                  })}
                >
                  {fieldColumns.map((c, idx) => {
                    const labelled = hasHeaderText(c);
                    return (
                      <MDBox
                        key={c.accessor}
                        role="cell"
                        display="grid"
                        gridTemplateColumns={
                          labelled ? "minmax(84px, 34%) minmax(0, 1fr)" : "minmax(0, 1fr)"
                        }
                        columnGap={1.5}
                        alignItems="center"
                        py={0.75}
                        sx={({ palette: { light }, borders: { borderWidth } }) => ({
                          borderBottom:
                            idx < fieldColumns.length - 1
                              ? `${borderWidth[1]} solid ${light.main}`
                              : "none",
                        })}
                      >
                        {labelled && (
                          <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            color="secondary"
                            textTransform="uppercase"
                            sx={{ fontSize: "0.65rem", letterSpacing: "0.04em", lineHeight: 1.3 }}
                          >
                            {c.Header}
                          </MDTypography>
                        )}
                        <MDBox
                          minWidth={0}
                          sx={({ typography: { size } }) => ({
                            fontSize: size.sm,
                            overflowWrap: "anywhere",
                          })}
                        >
                          {cells[c.accessor]}
                        </MDBox>
                      </MDBox>
                    );
                  })}
                  {actionColumns.length > 0 && (
                    <MDBox
                      display="flex"
                      flexWrap="wrap"
                      justifyContent="flex-end"
                      alignItems="center"
                      gap={1}
                      pt={1}
                      mt={0.25}
                      sx={({ palette: { light }, borders: { borderWidth } }) => ({
                        borderTop: `${borderWidth[1]} solid ${light.main}`,
                      })}
                    >
                      {actionColumns.map((c) => (
                        <Fragment key={c.accessor}>{cells[c.accessor]}</Fragment>
                      ))}
                    </MDBox>
                  )}
                </MDBox>
              );
            })}
          </MDBox>
        ) : (
          <Table>
            <MDBox component="thead">
              <TableRow>
                {columns.map((c) => (
                  <DataTableHeadCell
                    key={c.accessor}
                    width={c.width || "auto"}
                    align={c.align || "left"}
                    sorted={false}
                    sticky={tableOverflows && isActionColumn(c)}
                  >
                    {c.Header}
                  </DataTableHeadCell>
                ))}
              </TableRow>
            </MDBox>
            <TableBody>
              {result.items.map((item, index) => {
                const cells = renderRow(item);
                return (
                  <TableRow key={item._id || index}>
                    {columns.map((c) => (
                      <DataTableBodyCell
                        key={c.accessor}
                        align={c.align || "left"}
                        noBorder={index === result.items.length - 1}
                        breakAnywhere={c.breakAnywhere ?? isEmailColumn(c)}
                        sticky={tableOverflows && isActionColumn(c)}
                      >
                        {cells[c.accessor]}
                      </DataTableBodyCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        {!loading && !error && result.items.length === 0 && (
          <MDBox p={3} textAlign="center">
            <MDTypography variant="body2">
              {isFiltered ? "No records match these filters." : emptyMessage}
            </MDTypography>
          </MDBox>
        )}
        {loading && result.items.length === 0 && (
          <MDBox p={3} textAlign="center">
            <MDTypography variant="body2">Loading…</MDTypography>
          </MDBox>
        )}
      </MDBox>

      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={2}
        p={{ xs: 2, md: 3 }}
      >
        <MDBox display="flex" flexWrap="wrap" alignItems="center" gap={1.5}>
          <MDTypography variant="button" color="secondary" fontWeight="regular">
            {result.total === 0
              ? "No records"
              : `Showing ${firstRow}–${lastRow} of ${result.total.toLocaleString()}`}
          </MDTypography>
          <TextField
            select
            size="small"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            sx={{ width: 110 }}
          >
            {PAGE_SIZES.map((s) => (
              <MenuItem key={s} value={s}>
                {s} / page
              </MenuItem>
            ))}
          </TextField>
        </MDBox>
        {/* Three page numbers below md, so first/prev/pages/next/last fit a 360px phone. */}
        {result.totalPages > 1 && (
          <MDPagination variant="gradient" color="info">
            {page > 1 && (
              <>
                <MDPagination item onClick={() => setPage(1)}>
                  <Icon sx={{ fontWeight: "bold" }}>first_page</Icon>
                </MDPagination>
                <MDPagination item onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                </MDPagination>
              </>
            )}
            {pageWindow(page, result.totalPages, showCards ? 3 : 5).map((n) => (
              <MDPagination item key={n} active={n === page} onClick={() => setPage(n)}>
                {n}
              </MDPagination>
            ))}
            {page < result.totalPages && (
              <>
                <MDPagination
                  item
                  onClick={() => setPage((p) => Math.min(result.totalPages, p + 1))}
                >
                  <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                </MDPagination>
                <MDPagination item onClick={() => setPage(result.totalPages)}>
                  <Icon sx={{ fontWeight: "bold" }}>last_page</Icon>
                </MDPagination>
              </>
            )}
          </MDPagination>
        )}
      </MDBox>
    </TableContainer>
  );
}

const optionShape = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
});

RecordTable.defaultProps = {
  statusOptions: null,
  statusLabel: "Status",
  courseOptions: null,
  dateFields: null,
  reloadToken: 0,
  exportConfig: null,
  onLoaded: null,
  emptyMessage: "No records yet",
  defaultLimit: 20,
};

RecordTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.node.isRequired,
      accessor: PropTypes.string.isRequired,
      width: PropTypes.string,
      align: PropTypes.string,
    })
  ).isRequired,
  fetchPage: PropTypes.func.isRequired,
  renderRow: PropTypes.func.isRequired,
  statusOptions: PropTypes.arrayOf(optionShape),
  statusLabel: PropTypes.string,
  courseOptions: PropTypes.arrayOf(optionShape),
  dateFields: PropTypes.arrayOf(optionShape),
  reloadToken: PropTypes.number,
  exportConfig: PropTypes.shape({
    fetchAll: PropTypes.func.isRequired,
    toRows: PropTypes.func.isRequired,
    filename: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }),
  onLoaded: PropTypes.func,
  emptyMessage: PropTypes.string,
  defaultLimit: PropTypes.number,
};

export default RecordTable;
